import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search, Building2, User, Truck, Shield, Star, MapPin, Phone,
  Loader2, ChevronLeft, ChevronRight, CheckCircle, AlertTriangle,
  Users, Award, Filter, X, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const CompanyDirectoryPage = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');
  
  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 12;

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
        verified_only: verifiedOnly
      };
      
      if (search) params.search = search;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (serviceFilter !== 'all') params.service_type = serviceFilter;
      
      const response = await api.get('/directory/companies', { params });
      setProfiles(response.data.profiles);
      setTotal(response.data.total);
      setAccessDenied(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
        setAccessDeniedMessage(error.response?.data?.detail || 'A paid subscription is required to access the directory.');
      } else {
        toast.error('Failed to load company directory');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, serviceFilter, verifiedOnly]);

  const loadStats = async () => {
    try {
      const response = await api.get('/directory/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    loadStats();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadProfiles();
  };

  const viewProfile = async (userId) => {
    setLoadingProfile(true);
    setProfileDialogOpen(true);
    try {
      const response = await api.get(`/directory/company/${userId}`);
      setSelectedProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
      setProfileDialogOpen(false);
    } finally {
      setLoadingProfile(false);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      broker: { label: 'Broker', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      carrier: { label: 'Carrier', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      owner_operator: { label: 'Owner Operator', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
      fleet: { label: 'Fleet Owner', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
      shipper: { label: 'Shipper', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
      admin: { label: 'Admin', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
    };
    return badges[role] || { label: role, color: 'bg-gray-500/20 text-gray-600 border-gray-500/30' };
  };

  const getVerificationBadge = (status) => {
    if (status === 'verified') {
      return (
        <span className="flex items-center gap-1 text-xs text-green-400">
          <CheckCircle className="w-3 h-3" />
          Verified
        </span>
      );
    }
    return null;
  };

  const getFraudShieldBadge = (status) => {
    if (status === 'trusted') {
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-[#4ADE80]/20 text-green-600 rounded-full border border-[#4ADE80]/30">
          <Shield className="w-3 h-3" />
          Trusted
        </span>
      );
    } else if (status === 'flagged') {
      return (
        <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full border border-yellow-500/30">
          <AlertTriangle className="w-3 h-3" />
          Flagged
        </span>
      );
    }
    return null;
  };

  const totalPages = Math.ceil(total / limit);

  // Show access denied message if user doesn't have paid subscription
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-100 pt-20 pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="bg-white border-gray-200 text-center py-12">
            <CardContent>
              <div className="w-20 h-20 rounded-full bg-[#3B82F6]/20 flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-10 h-10 text-[#3B82F6]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0D1B2A] mb-4">Directory Access Required</h2>
              <p className="text-[#94A3B8] mb-6 max-w-md mx-auto">
                {accessDeniedMessage}
              </p>
              <Link to="/pricing">
                <Button className="bg-gradient-to-r from-[#4ADE80] to-[#22C55E] hover:from-[#86EFAC] hover:to-[#4ADE80] text-black font-semibold px-8">
                  View Subscription Plans
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6]/20 to-[#4ADE80]/20 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-[#0D1B2A]" style={{fontFamily: 'Poppins, sans-serif'}}>
                Company Directory
              </h1>
              <p className="text-[#64748B]">Find and connect with auto transport professionals</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-white border-gray-200" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 text-[#60A5FA] mx-auto mb-2" />
                <div className="text-2xl font-bold text-[#0D1B2A]">{stats.total_users}</div>
                <div className="text-xs text-[#64748B]">Total Users</div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
              <CardContent className="p-4 text-center">
                <Building2 className="w-6 h-6 text-[#3B82F6] mx-auto mb-2" />
                <div className="text-2xl font-bold text-[#0D1B2A]">{stats.total_brokers}</div>
                <div className="text-xs text-[#64748B]">Brokers</div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
              <CardContent className="p-4 text-center">
                <Truck className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-[#0D1B2A]">{stats.total_carriers}</div>
                <div className="text-xs text-[#64748B]">Carriers</div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-[#0D1B2A]">{stats.total_verified}</div>
                <div className="text-xs text-[#64748B]">Verified</div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
              <CardContent className="p-4 text-center">
                <Award className="w-6 h-6 text-[#F59E0B] mx-auto mb-2" />
                <div className="text-2xl font-bold text-[#0D1B2A]">{stats.total_companies}</div>
                <div className="text-xs text-[#64748B]">Companies</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search and Filters */}
        <Card className="bg-white border-gray-200 mb-6" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                <Input
                  placeholder="Search by company, name, MC#, or region..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-11 bg-white border-gray-200 text-[#0D1B2A]"
                  data-testid="directory-search-input"
                />
              </div>
              
              <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(0); }}>
                <SelectTrigger className="w-full md:w-40 h-11 bg-white border-gray-200 text-[#0D1B2A]">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="broker">Brokers</SelectItem>
                  <SelectItem value="carrier">Carriers</SelectItem>
                  <SelectItem value="owner_operator">Owner Operators</SelectItem>
                  <SelectItem value="fleet">Fleet Owners</SelectItem>
                  <SelectItem value="shipper">Shippers</SelectItem>
                </SelectContent>
              </Select>

              <Select value={serviceFilter} onValueChange={(v) => { setServiceFilter(v); setPage(0); }}>
                <SelectTrigger className="w-full md:w-44 h-11 bg-white border-gray-200 text-[#0D1B2A]">
                  <SelectValue placeholder="Service Type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="all">All Services</SelectItem>
                  <SelectItem value="open_transport">Open Transport</SelectItem>
                  <SelectItem value="enclosed_transport">Enclosed Transport</SelectItem>
                  <SelectItem value="expedited">Expedited</SelectItem>
                  <SelectItem value="dealer_services">Dealer Services</SelectItem>
                </SelectContent>
              </Select>

              <Button
                type="button"
                variant={verifiedOnly ? "default" : "outline"}
                onClick={() => { setVerifiedOnly(!verifiedOnly); setPage(0); }}
                className={verifiedOnly 
                  ? "bg-[#4ADE80] text-black hover:bg-[#22C55E]" 
                  : "border-[#3B82F6]/30 text-[#0D1B2A] hover:bg-[#3B82F6]/10"
                }
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Verified Only
              </Button>

              <Button
                type="submit"
                className="bg-gradient-to-r from-[#4ADE80] to-[#22C55E] text-black hover:from-[#86EFAC] hover:to-[#4ADE80]"
                style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.3)'}}
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
          </div>
        ) : profiles.length === 0 ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
              <p className="text-[#94A3B8]">No companies found matching your criteria</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setRoleFilter('all');
                  setServiceFilter('all');
                  setVerifiedOnly(false);
                  setPage(0);
                }}
                className="mt-4 border-[#3B82F6]/30 text-[#0D1B2A]"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {profiles.map((profile) => {
                const roleBadge = getRoleBadge(profile.role);
                return (
                  <Card
                    key={profile.user_id}
                    className="bg-white border-gray-200 hover:border-[#4ADE80]/40 transition-all cursor-pointer group"
                    style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}
                    onClick={() => viewProfile(profile.user_id)}
                    data-testid={`company-card-${profile.user_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                          {profile.avatar_url ? (
                            <img src={profile.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <User className="w-6 h-6 text-[#64748B]" />
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#0D1B2A] truncate group-hover:text-green-600 transition-colors">
                            {profile.company_name || profile.full_name || 'Unknown'}
                          </h3>
                          {profile.company_name && profile.full_name && (
                            <p className="text-sm text-[#64748B] truncate">{profile.full_name}</p>
                          )}
                          
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${roleBadge.color}`}>
                              {roleBadge.label}
                            </span>
                            {getVerificationBadge(profile.verification_status)}
                            {getFraudShieldBadge(profile.fraud_shield_status)}
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="mt-4 space-y-2">
                        {profile.region && (
                          <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                            <MapPin className="w-4 h-4 text-[#60A5FA]" />
                            <span className="truncate">{profile.region}</span>
                          </div>
                        )}
                        {profile.mc_number && (
                          <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                            <Shield className="w-4 h-4 text-green-600" />
                            <span>MC# {profile.mc_number}</span>
                          </div>
                        )}
                        {profile.rating > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            <span className="text-[#0D1B2A]">{profile.rating.toFixed(1)}</span>
                            <span className="text-[#64748B]">({profile.rating_count} reviews)</span>
                          </div>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-4 text-[#60A5FA] hover:text-[#0D1B2A] hover:bg-[#3B82F6]/10"
                      >
                        View Profile
                        <ExternalLink className="w-4 h-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-[#3B82F6]/30 text-[#0D1B2A] disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="text-[#94A3B8]">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="border-[#3B82F6]/30 text-[#0D1B2A] disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Profile Detail Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="bg-white border-gray-200 max-w-lg" style={{boxShadow: '0 8px 32px rgba(59, 130, 246, 0.2)'}}>
            <DialogHeader>
              <DialogTitle className="text-[#0D1B2A]">Company Profile</DialogTitle>
            </DialogHeader>
            
            {loadingProfile ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
              </div>
            ) : selectedProfile && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center">
                    {selectedProfile.avatar_url ? (
                      <img src={selectedProfile.avatar_url} alt="" className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-[#64748B]" />
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#0D1B2A]">
                      {selectedProfile.company_name || selectedProfile.full_name}
                    </h2>
                    {selectedProfile.company_name && selectedProfile.full_name && (
                      <p className="text-[#94A3B8]">{selectedProfile.full_name}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      {getRoleBadge(selectedProfile.role) && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${getRoleBadge(selectedProfile.role).color}`}>
                          {getRoleBadge(selectedProfile.role).label}
                        </span>
                      )}
                      {getVerificationBadge(selectedProfile.verification_status)}
                      {getFraudShieldBadge(selectedProfile.fraud_shield_status)}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                {selectedProfile.stats && (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-[#0D1B2A]">{selectedProfile.stats.posted_loads}</div>
                      <div className="text-xs text-[#64748B]">Loads Posted</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-green-600">{selectedProfile.stats.completed_loads}</div>
                      <div className="text-xs text-[#64748B]">Completed</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-[#60A5FA]">{selectedProfile.stats.hauled_loads}</div>
                      <div className="text-xs text-[#64748B]">Hauled</div>
                    </div>
                  </div>
                )}

                {/* Details */}
                <div className="space-y-3">
                  {selectedProfile.phone && (
                    <div className="flex items-center gap-3 text-[#94A3B8]">
                      <Phone className="w-5 h-5 text-[#60A5FA]" />
                      <span>{selectedProfile.phone}</span>
                    </div>
                  )}
                  {selectedProfile.region && (
                    <div className="flex items-center gap-3 text-[#94A3B8]">
                      <MapPin className="w-5 h-5 text-[#60A5FA]" />
                      <span>{selectedProfile.region}</span>
                    </div>
                  )}
                  {selectedProfile.mc_number && (
                    <div className="flex items-center gap-3 text-[#94A3B8]">
                      <Shield className="w-5 h-5 text-green-600" />
                      <span>MC# {selectedProfile.mc_number}</span>
                    </div>
                  )}
                  {selectedProfile.dot_number && (
                    <div className="flex items-center gap-3 text-[#94A3B8]">
                      <Shield className="w-5 h-5 text-green-600" />
                      <span>DOT# {selectedProfile.dot_number}</span>
                    </div>
                  )}
                  {selectedProfile.service_type && (
                    <div className="flex items-center gap-3 text-[#94A3B8]">
                      <Truck className="w-5 h-5 text-[#60A5FA]" />
                      <span className="capitalize">{selectedProfile.service_type.replace(/_/g, ' ')}</span>
                    </div>
                  )}
                  {selectedProfile.rating > 0 && (
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                      <span className="text-[#0D1B2A] font-medium">{selectedProfile.rating.toFixed(1)}</span>
                      <span className="text-[#64748B]">({selectedProfile.rating_count} reviews)</span>
                    </div>
                  )}
                </div>

                {selectedProfile.bio && (
                  <div>
                    <h4 className="text-sm font-medium text-[#0D1B2A] mb-2">About</h4>
                    <p className="text-[#94A3B8] text-sm">{selectedProfile.bio}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CompanyDirectoryPage;
